<template>
    <Form
        :schema="LoginSchema"
        :form-values="login"
        :is-busy="isLoading"
        :submit-text="signInButtonText"
        @submit="onFormSubmit"
        @reset="resetForm">
        <Alert v-if="errorMessage" variant="danger">
            <AlertDescription>{{ errorMessage }}</AlertDescription>
        </Alert>

        <FormInput v-model="login.UserName" :label="t('SignIn.UserNameLabelText')" field="UserName" />

        <FormInput v-model="login.Password" :label="t('Global.PasswordLabel')" type="password" field="Password" />

        <div>
            <a :href="localePath('/forgot-password')">{{ $t("SignIn.ForgotPasswordText") }}</a>
        </div>

        <div>
            {{ $t("SignIn.CreateAccount") }}
            <a :href="localePath('/register')">{{ $t("SignIn.SignupLink") }}</a>
        </div>
    </Form>
</template>
<script setup lang="ts">
import { safeParse } from "valibot";

const { t } = useI18n();
const guestCookie = useGuestCookie();
const { logIn } = useIdentity();
const initializeState = useInitializeState();
const { importAnonymousBOM } = useBom();
const localePath = useLangPath();
const { LoginSchema } = useSchemas();
const route = useRoute();
const loginStatus = useStateLoginStatus();

const initialValuesLogin = { UserName: "", Password: "" };

const login = ref<LogInRequest>({ ...initialValuesLogin });
const errorMessage = ref("");
const isLoading = ref(false);

const emit = defineEmits<{ success: [response: LogInResponse]; complete: [response: LogInResponse] }>();

const signInButtonText = computed(() => t(isLoading.value ? "SignIn.LoginButtonLoading" : "Global.SignInLabel"));

const parseResult = computed(() => safeParse(LoginSchema, login.value));
const isValid = computed(() => parseResult.value.success);

async function onFormSubmit() {
    if (!isValid.value) return;

    isLoading.value = true;
    loginStatus.value = LoginStatus.Pending;
    try {
        const logInRequest = parseResult.value.output as LogInRequest;
        // Handle both PascalCase and camelCase query parameter formats for compatibility
        const returnUrl = route.query.ReturnUrl?.toString() ?? route.query.returnUrl?.toString();
        const logInResponse = await logIn(logInRequest, returnUrl);

        // emit success event first because otherwise changing auth state destroys BomPartAddAnon
        emit("success", logInResponse);
        loginStatus.value = LoginStatus.Success;
        await initializeState();
        await importAnonymousBOM();

        guestCookie.value = null; // delete cookie
        emit("complete", logInResponse); // emit complete last so that we don't redirect away too soon
        loginStatus.value = LoginStatus.Complete;
    } catch (err) {
        loginStatus.value = LoginStatus.Error;
        const error = err as Error;
        errorMessage.value = error.message || t("Global.GenericError");
    } finally {
        isLoading.value = false;
    }
}

function resetForm() {
    login.value = { ...initialValuesLogin };
}
</script>
